import { useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'react-toastify'
import { Loader } from '../../components/Loader/Loader'
import {
  useFetchAllGenerationPrompts,
  useUpdateGenerationPromptById,
  type Nullish,
  type Prompts,
} from '../api/apiHooks'
import { BaseHeader } from '../components/BaseHeader'
import { TextAreaField } from '../components/ControlledTextAreaField'
import { DescriptionSection } from '../components/DescriptionSection/DescriptionSection'
import { DisclaimerSection } from '../components/DisclaimerSection'
import { PromptSelect } from '../components/PromptSelect'
import { ResponseFormatDisplay } from '../components/ResponseFormatDisplay'
import { SubmitButton } from '../components/SubmitButton'
import { UiAlertMessage } from '../components/UiAlertMessage'

export function PromptPage() {
  const queryClient = useQueryClient()
  const [isEditingDescription, setIsEditingDescription] = useState(false)
  const [responseFormatString, setResponseFormatString] = useState('')

  const {
    data: allPrompts,
    isLoading,
    isFetched,
    isError,
  } = useFetchAllGenerationPrompts()

  const {
    control,
    register,
    handleSubmit,
    watch,
    reset,
    formState: { isDirty, dirtyFields, isSubmitting },
  } = useForm<Prompts>({
    defaultValues: {
      user_prompt: '',
      system_prompt: '',
      extra_context: '',
      description: '',
    },
  })

  const selectedPromptId = watch('id')
  const selectedPrompt = allPrompts?.data?.find(p => p.id === selectedPromptId)

  const updatePrompt = useUpdateGenerationPromptById(selectedPromptId)

  // Set default prompt when data loads
  useEffect(() => {
    if (allPrompts?.data && allPrompts.data.length > 0 && !selectedPromptId) {
      const firstPrompt = allPrompts.data[0]
      reset({
        id: firstPrompt.id,
        user_prompt: firstPrompt.user_prompt || '',
        system_prompt: firstPrompt.system_prompt || '',
        extra_context: firstPrompt.extra_context || '',
        description: firstPrompt.description || '',
      })
    }
  }, [allPrompts, selectedPromptId, reset])

  // Update form when selected prompt changes
  useEffect(() => {
    if (selectedPrompt) {
      reset({
        id: selectedPrompt.id,
        user_prompt: selectedPrompt.user_prompt || '',
        system_prompt: selectedPrompt.system_prompt || '',
        extra_context: selectedPrompt.extra_context || '',
        description: selectedPrompt.description || '',
      })
      setIsEditingDescription(false)
    }
  }, [selectedPrompt, reset])

  // Stringify response format for display
  useEffect(() => {
    try {
      if (selectedPrompt?.response_format) {
        setResponseFormatString(
          JSON.stringify(selectedPrompt.response_format, null, 2),
        )
      } else {
        setResponseFormatString('')
      }
    } catch (error) {
      console.error('Error stringify response format:', error)
      setResponseFormatString('')
    }
  }, [selectedPrompt?.response_format])

  const onSubmit = (data: Nullish<Prompts & { description?: string }>) => {
    // Create an object with only the changed fields
    const changedFields: Nullish<Prompts> = {}

    if (dirtyFields.user_prompt) {
      changedFields.user_prompt = data.user_prompt?.trim() || null
    }

    if (dirtyFields.system_prompt) {
      changedFields.system_prompt = data.system_prompt?.trim() || null
    }

    if (dirtyFields.extra_context) {
      changedFields.extra_context = data.extra_context?.trim() || null
    }

    if (dirtyFields.description) {
      changedFields.description = data.description?.trim() || null
    }

    updatePrompt.mutate(changedFields, {
      onSuccess(response) {
        console.log('Prompt updated successfully:', response)

        if (selectedPrompt) {
          reset({
            id: selectedPrompt.id,
            user_prompt: data.user_prompt || '',
            system_prompt: data.system_prompt || '',
            extra_context: data.extra_context || '',
            description: data.description || '',
          })
        }

        setIsEditingDescription(false)

        queryClient
          .invalidateQueries({ queryKey: ['get-all-generation-prompts'] })
          .catch(error => {
            console.error('Error invalidating prompts cache:', error)
          })

        toast.success('Prompt updated successfully!')
      },
      onError(error) {
        console.error('Error updating prompt:', error)
        toast.error('Failed to update prompt. Please try again.')
      },
    })
  }

  if (isLoading) {
    return <Loader label="" />
  }

  if (isError) {
    return (
      <UiAlertMessage
        variant="error"
        message="Error loading prompts. Please try again."
      />
    )
  }

  if (isFetched && (!allPrompts?.data || allPrompts.data.length === 0)) {
    return <UiAlertMessage variant="info" message="No prompts found." />
  }

  return (
    <div className="tc-mv-main-wrapper pb-8">
      <div className="h-full mt-0 pt-[88px]">
        <div className="text-white tc-mv-card-wrapper">
          <BaseHeader title="Prompts" />

          <div className="mt-2">
            <div className="relative">
              <div className="-z-10 absolute bottom-0 left-0 right-0 tc-mv-blur top-0"></div>
              <div className="p-4">
                <form
                  onSubmit={handleSubmit(onSubmit)}
                  className="min-h-[calc(100vh-210px)]"
                >
                  <PromptSelect
                    control={control}
                    allPrompts={allPrompts}
                    selectedPrompt={selectedPrompt}
                  />

                  <DescriptionSection
                    isEditingDescription={isEditingDescription}
                    setIsEditingDescription={setIsEditingDescription}
                    selectedPrompt={selectedPrompt}
                    register={register}
                    dirtyFields={dirtyFields}
                  />

                  <TextAreaField
                    name="system_prompt"
                    label="System Prompt"
                    register={register}
                    dirtyFields={dirtyFields}
                    placeholder="Edit the system prompt here..."
                  />

                  <TextAreaField
                    name="user_prompt"
                    label="User Prompt"
                    register={register}
                    dirtyFields={dirtyFields}
                    placeholder="Edit the user prompt here..."
                  />

                  <TextAreaField
                    name="extra_context"
                    label="Extra Context"
                    register={register}
                    dirtyFields={dirtyFields}
                    placeholder="Edit the extra context here..."
                  />

                  <ResponseFormatDisplay
                    responseFormatString={responseFormatString}
                  />

                  <DisclaimerSection />

                  <SubmitButton
                    isDirty={isDirty}
                    isSubmitting={isSubmitting}
                    isPending={updatePrompt.isPending}
                  />
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
